/**
 * MemberSelectionList Component - Story 4.5
 *
 * Displays a list of members with checkboxes for selection
 * Used in Step 2 (Distribute) of the coupon creation wizard
 */

import React from 'react';
import { User } from 'lucide-react';
import type { MemberSelectionListProps } from '../../../types/ui.types';

/**
 * Simple flat list of members with checkboxes for selection
 * No virtualization needed for ~20 members max (MVP)
 */
export function MemberSelectionList({
	members,
	selectedUserIds,
	onToggleSelection,
	isLoading,
	disabled = false,
}: MemberSelectionListProps) {
	if (isLoading) {
		return (
			<div className="space-y-2">
				{/* Loading skeleton */}
				{[1, 2, 3].map((i) => (
					<div
						key={i}
						className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 animate-pulse"
					>
						<div className="w-5 h-5 bg-gray-200 rounded" />
						<div className="w-8 h-8 bg-gray-200 rounded-full" />
						<div className="flex-1">
							<div className="h-4 bg-gray-200 rounded w-24 mb-1" />
							<div className="h-3 bg-gray-200 rounded w-20" />
						</div>
					</div>
				))}
			</div>
		);
	}

	return (
		<div className="space-y-2">
			{members.map((member) => {
				const isSelected = selectedUserIds.has(member.user_id);
				const displayName = member.display_name || member.phone_masked;

				return (
					<label
						key={member.user_id}
						className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
							disabled
								? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
								: isSelected
									? 'border-blue-500 bg-blue-50'
									: 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
						}`}
					>
						{/* Checkbox */}
						<input
							type="checkbox"
							checked={isSelected}
							onChange={() => onToggleSelection(member.user_id)}
							disabled={disabled}
							className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 disabled:opacity-50"
						/>

						{/* Avatar placeholder */}
						<div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
							<User className="w-4 h-4 text-gray-400" />
						</div>

						{/* Member info */}
						<div className="flex-1 min-w-0">
							<p className="text-sm font-medium text-gray-900 truncate">
								{displayName}
							</p>
							{member.display_name && (
								<p className="text-xs text-gray-500 truncate">
									{member.phone_masked}
								</p>
							)}
						</div>
					</label>
				);
			})}
		</div>
	);
}

export default MemberSelectionList;
